import { AxiosResponse } from 'axios';
import { Employee } from '../../models/employee';
import { Project } from '../../models/project';
import {
  EmployeeAllocations,
  ProjectAllocations,
  ReportSource,
} from '../../models/reports';
import {
  EmployeesSummary,
  GetSummaryPayload,
  ProjectsSummary,
} from '../../models/reports';
import { ApiResponse, httpService } from '../httpService';
import { Report } from '../../models/report';

export interface ReportParams {
  startDate: string;
  endDate: string;
  employeeIds?: string[];
  projectIds?: string[];
  userRoleId?: string;
  employeeId?: string;
  showHierarchy?: boolean;
  reportSource?: ReportSource;
  includeInactive?: boolean;
}

export interface GetEmployeesParams {
  filter?: string;
  userRoleId?: string;
  managerId?: string;
  skip?: number;
  take?: number;
  includeInactive?: boolean;
  includeDeleted?: boolean;
  showHierarchy?: boolean;
}

export interface GetProjectsParams {
  filter?: string;
  employeeId?: string;
  userRoleId?: string;
  managerId?: string;
  skip?: number;
  take?: number;
  includeDeleted?: boolean;
}

export interface GetAllocationsFilteringPayload {
  startDate: string;
  endDate: string;
  employeeId: string | null;
  projectId: string | null;
  managerId: string | null;
  userRoleId?: string;
}

interface GetPeopleAllocationsFilteringPayload
  extends GetAllocationsFilteringPayload {
  showHierarchy: boolean;
  includeInactive: boolean;
  includeDeleted: boolean;
}

export interface GetAllocationsDefaultPayload {
  skip: number | null;
  take: number | null;
  filter: string | null;
  includeDeleted: boolean;
}

export interface GetProjectsParams {
  filter?: string;
  employeeId?: string;
  userRoleId?: string;
  managerId?: string;
  skip?: number;
  take?: number;
  includeDeleted?: boolean;
}

export enum ExcelReportsUrls {
  DETAILS = 'report-details-excel',
  INTERDEP = 'report-interdep-excel',
  SYNTHESIS = 'report-synthesis-excel',
  PROJECT_ACTIVITY = 'report-projects-activities-excel',
}

export const ReportsApiURL = '/api/Reports';

export async function downloadExcelReport(
  report: Report
): Promise<AxiosResponse> {
  const result = await httpService.post<ReportParams, ApiResponse<unknown>>(
    `${ReportsApiURL}/${report.apiUrl}`,
    report.buildPayload(),
    {
      responseType: 'blob',
    }
  );

  return result;
}

export const getEmployees = async (
  params: GetEmployeesParams
): Promise<ApiResponse<Employee>> => {
  const response = await httpService.get<ApiResponse<Employee>>(
    `${ReportsApiURL}/employees`,
    {
      params,
    }
  );

  return response.data;
};

export const getEmployeesByProjectId = async (
  projectId: string,
  params: GetEmployeesParams
): Promise<ApiResponse<Employee>> => {
  const response = await httpService.get<ApiResponse<Employee>>(
    `${ReportsApiURL}/employees/${projectId}`,
    {
      params,
    }
  );

  return response.data;
};

export const getProjects = async (
  params: GetProjectsParams
): Promise<ApiResponse<Project>> => {
  const response = await httpService.get<ApiResponse<Project>>(
    `${ReportsApiURL}/projects`,
    {
      params,
    }
  );

  return response.data;
};

export const getProjectsByEmployeeId = async (
  employeeId: string,
  params: GetProjectsParams
): Promise<ApiResponse<Project>> => {
  const response = await httpService.get<ApiResponse<Project>>(
    `${ReportsApiURL}/projects/${employeeId}`,
    {
      params,
    }
  );

  return response.data;
};

export const getEmployeesSummary = async (
  payload: GetSummaryPayload,
  loggedHoursMismatch: boolean
): Promise<ApiResponse<EmployeesSummary>> => {
  delete payload.monthSelect;
  const response = await httpService.post<
    GetSummaryPayload,
    ApiResponse<EmployeesSummary>
  >(`${ReportsApiURL}/employees/summary`, payload, {
    params: { loggedHoursMismatch },
  });

  return response.data;
};

export const getProjectsSummary = async (
  payload: GetSummaryPayload,
  loggedHoursProjects: boolean
): Promise<ApiResponse<ProjectsSummary>> => {
  delete payload.monthSelect;
  const response = await httpService.post<
    GetSummaryPayload,
    ApiResponse<ProjectsSummary>
  >(`${ReportsApiURL}/projects/summary`, payload, {
    params: { loggedHoursProjects },
  });

  return response.data;
};

const allocationsPayloadPartial = {
  skip: null,
  take: null,
  filter: null,
};

export const getEmployeesAllocations = async (
  payload: GetPeopleAllocationsFilteringPayload
): Promise<ApiResponse<EmployeeAllocations>> => {
  const response = await httpService.post<
    GetAllocationsDefaultPayload,
    ApiResponse<EmployeeAllocations>
  >(`${ReportsApiURL}/employees/allocations`, {
    ...allocationsPayloadPartial,
    ...payload,
  });

  return response.data;
};

export const getProjectsAllocations = async (
  payload: GetAllocationsFilteringPayload,
  loggedHoursProjects: boolean
): Promise<ApiResponse<ProjectAllocations>> => {
  const response = await httpService.post<
    GetAllocationsDefaultPayload,
    ApiResponse<ProjectAllocations>
  >(
    `${ReportsApiURL}/projects/allocations`,
    {
      includeDeleted: false,
      ...allocationsPayloadPartial,
      ...payload,
    },
    {
      params: { loggedHoursProjects },
    }
  );

  return response.data;
};
